import { iOrder } from "core/kangarooTypes/iOrder";
import { iPaginationResponse } from "core/kangarooTypes/iPaginationResponse";
import {
  getOrders,
  importOrders,
  ImportResult,
  downloadOrderTemplate,
} from "core/Services/MerchantServices";
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  Download,
  Eye,
  Plus,
  Search,
  Upload,
  X,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Button from "shared/Button";
import FormInput from "shared/FormInput";
import { USER_ID, USER_ROLE } from "utils/constant";
import { formatDate, formatStatus } from "utils/DatesUtil";
import { getStatusColors } from "utils/statusColors";
import { useTranslation } from "react-i18next";

const MerchantOrders = () => {
  const { t } = useTranslation();
  const { driver_id, merchant_id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [orders, setOrders] = useState<iPaginationResponse<iOrder>>();
  const [filters, setFilters] = useState({
    order: "",
    status: "",
    orderType: "",
    dateFrom: "",
    dateTo: "",
    page: 1,
    date_type: "created_at",
    sort_field: "",
    sort_dir: "",
  });
  const [userRole, setUserRole] = useState<string>("");

  // Import state
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const fileInputRef = useState<HTMLInputElement | null>(null)[0];

  useEffect(() => {
    const role = localStorage.getItem(USER_ROLE);
    if (role) {
      setUserRole(role);
    }
  }, []);

  type FiltersType = {
    order: string;
    status: string;
    orderType: string;
    dateFrom: string;
    dateTo: string;
    page: number;
    date_type: string;
    sort_field: string;
    sort_dir: string;
  };

  const cleanFilters = (filters: FiltersType) => {
    return Object.entries(filters).reduce((acc, [key, value]) => {
      if (value !== "" && value !== undefined && value !== null) {
        return { ...acc, [key]: value };
      }
      return acc;
    }, {});
  };

  const fetchOrders = async (newFilters: typeof filters) => {
    try {
      setIsLoading(true);
      const userId = await localStorage.getItem(USER_ID);
      const merchantId = merchant_id || userId!;
      const cleanedFilters = cleanFilters(newFilters);
      const response = await getOrders(
        {
          ...cleanedFilters,
          driver_id,
          merchant_id: driver_id ? undefined : merchantId,
        },
        newFilters.page
      );
      if (response) {
        setOrders(response);
      }
    } catch (error) {
      toast.error("Failed to load orders.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders(filters);
  }, [filters]);

  const {
    register,
    formState: { errors },
    watch,
    reset,
  } = useForm({
    defaultValues: {
      order: "",
      status: "",
      orderType: "",
      dateFrom: "",
      dateTo: "",
      date_type: "created_at",
    },
  });

  const handleClearFilters = () => {
    reset();
    setFilters({
      order: "",
      status: "",
      orderType: "",
      dateFrom: "",
      dateTo: "",
      page: 1,
      date_type: "created_at",
      sort_field: "",
      sort_dir: "",
    });
  };

  useEffect(() => {
    const subscription = watch((values) => {
      const newFilters = {
        ...filters,
        ...values,
        page: 1,
      };

      setFilters(newFilters);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({
      ...prev,
      page,
    }));
  };

  const handleSort = (field: string) => {
    setFilters((prev) => ({
      ...prev,
      sort_field: field,
      sort_dir:
        prev.sort_field === field && prev.sort_dir === "asc" ? "desc" : "asc",
    }));
  };

  const SortHeader = ({ field, label }: { field: string; label: string }) => {
    const isActive = filters.sort_field === field;
    return (
      <th
        onClick={() => handleSort(field)}
        className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none"
      >
        <div className="flex items-center space-x-1">
          <span>{label}</span>
          <div className="flex flex-col">
            {isActive && filters.sort_dir === "asc" ? (
              <ArrowUp size={14} />
            ) : null}
            {isActive && filters.sort_dir === "desc" ? (
              <ArrowDown size={14} />
            ) : null}
            {!isActive ? (
              <ArrowDown size={14} className="text-gray-300" />
            ) : null}
          </div>
        </div>
      </th>
    );
  };

  // Fulfillment workflow statuses
  const statusOptions = [
    {
      value: "ready_for_packaging",
      label: t("merchantOrders.statuses.readyForPackaging"),
    },
    { value: "on_hold", label: t("merchantOrders.statuses.onHold") },
    {
      value: "ready_for_shipping",
      label: t("merchantOrders.statuses.readyForShipping"),
    },
    { value: "shipped", label: t("merchantOrders.statuses.shipped") },
    {
      value: "ship_partially",
      label: t("merchantOrders.statuses.shipPartially"),
    },
    { value: "handed_off", label: t("merchantOrders.statuses.handedOff") },
    { value: "canceled", label: t("merchantOrders.statuses.canceled") },
  ];

  const handleCreateDeliveryOrder = () => {
    // If admin is creating order for merchant, pass merchant_id
    if (merchant_id) {
      navigate(
        `/order-form?pageTitle=Delivery Order&merchantId=${merchant_id}`
      );
    } else {
      navigate("/order-form?pageTitle=Delivery Order");
    }
  };

  const handleOrderDetails = (id: number) => {
    // Pass merchantId in URL if admin/operator viewing merchant orders
    if (merchant_id) {
      navigate(
        `/order-details/:id=${id}?pageTitle=Order Details&merchantId=${merchant_id}`
      );
    } else {
      navigate(`/order-details/:id=${id}?pageTitle=Order Details`);
    }
  };

  const handleExportCSV = async () => {
    try {
      const cleanedFilters = cleanFilters(filters);
      await getOrders({ ...cleanedFilters, export: true }, filters.page);
    } catch (error) {
      toast.error("Failed to export orders.");
    }
  };

  // Import handlers
  const handleFileSelect = async (file: File) => {
    if (!file || !merchant_id) {
      return;
    }

    // Validate file type
    const validTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];
    if (!validTypes.includes(file.type)) {
      toast.error("Please select a valid Excel file (.xlsx or .xls)");
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }

    setIsImporting(true);
    setImportResult(null);
    try {
      const response = await importOrders(file, parseInt(merchant_id));
      setImportResult(response.data);

      fetchOrders(filters); // Refresh orders list
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to import orders");
    } finally {
      setIsImporting(false);
    }
  };

  const handleImportClick = () => {
    // Create a temporary file input
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".xlsx,.xls";
    input.onchange = (e: Event) => {
      const target = e.target as HTMLInputElement;
      const file = target.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    };
    input.click();
  };

  const handleDownloadTemplate = async () => {
    setIsDownloadingTemplate(true);
    try {
      await downloadOrderTemplate();
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const clearImport = () => {
    setImportResult(null);
  };

  const getStatusColorClasses = (status: string) => {
    const colors = getStatusColors(status);
    return `${colors.bg} ${colors.text}`;
  };

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {!driver_id && (userRole === "merchant" || userRole === "admin") && (
            <Button
              onClick={handleCreateDeliveryOrder}
              icon={<Plus size={20} />}
              className="w-full sm:w-auto"
            >
              {t("merchantOrders.createOrder")}
            </Button>
          )}
          {/* Import Order Button - Only for admin/operator */}
          {(userRole === "admin" || userRole === "operator") && merchant_id && (
            <Button
              onClick={handleImportClick}
              icon={
                isImporting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Upload size={20} />
                )
              }
              className="w-full sm:w-auto"
              disabled={isImporting}
            >
              {isImporting ? "Importing..." : "Import Orders"}
            </Button>
          )}
        </div>
        <Button
          icon={<Download size={16} />}
          onClick={handleExportCSV}
          className="w-full sm:w-auto"
        >
          {t("merchantOrders.export")}
        </Button>
      </div>

      {/* Download Template Button - Only for admin/operator */}
      {(userRole === "admin" || userRole === "operator") && merchant_id && (
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">
              Need a template? Download the Excel template to get started with
              bulk imports.
            </p>
            <Button
              variant="secondary"
              onClick={handleDownloadTemplate}
              icon={
                isDownloadingTemplate ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download size={16} />
                )
              }
              disabled={isDownloadingTemplate}
              className="ml-4"
            >
              {isDownloadingTemplate ? "Downloading..." : "Download Template"}
            </Button>
          </div>
        </div>
      )}

      {/* Import Results */}
      {importResult && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Import Results</h2>
            <button
              onClick={clearImport}
              className="p-2 hover:bg-gray-200 rounded-full"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-600">Total Orders</p>
              <p className="text-2xl font-bold text-blue-800">
                {importResult.total_orders}
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-green-600">Ready for Packaging</p>
              <p className="text-2xl font-bold text-green-800">
                {importResult.ready_for_packaging}
              </p>
            </div>
            <div className="p-4 bg-yellow-50 rounded-lg">
              <p className="text-sm text-yellow-600">On Hold</p>
              <p className="text-2xl font-bold text-yellow-800">
                {importResult.on_hold}
              </p>
            </div>
          </div>

          {/* Errors */}
          {importResult.errors.length > 0 && (
            <div className="mb-6">
              <h3 className="font-medium text-red-600 mb-2 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Errors ({importResult.errors.length})
              </h3>
              <div className="bg-red-50 rounded-lg p-4 max-h-40 overflow-y-auto">
                {importResult.errors.map((error, index) => (
                  <p key={index} className="text-sm text-red-700">
                    Order {error.order_id}: {error.message}
                  </p>
                ))}
              </div>
            </div>
          )}

          {/* Imported Orders Table */}
          <div>
            <h3 className="font-medium mb-2">Imported Orders</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="text-left p-3">System ID</th>
                    <th className="text-left p-3">Excel Order #</th>
                    <th className="text-left p-3">Customer</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-left p-3">Items</th>
                    <th className="text-left p-3">Available</th>
                    <th className="text-left p-3">Missing SKUs</th>
                  </tr>
                </thead>
                <tbody>
                  {importResult.orders.map((order) => (
                    <tr
                      key={order.order_id}
                      className="border-b hover:bg-gray-50 cursor-pointer"
                      onClick={() =>
                        navigate(
                          `/order-details/:id=${order.order_id}?pageTitle=Order Details`
                        )
                      }
                    >
                      <td className="p-3">{order.order_id}</td>
                      <td className="p-3">
                        <span className="font-mono text-blue-600">
                          {order.external_order_number || order.order_id}
                        </span>
                      </td>
                      <td className="p-3">{order.customer_name || "-"}</td>
                      <td className="p-3">
                        <span
                          className={`px-2 py-1 rounded-full text-xs ${
                            order.status === "ready_for_packaging"
                              ? "bg-green-100 text-green-800"
                              : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {order.status.replace(/_/g, " ")}
                        </span>
                      </td>
                      <td className="p-3">{order.items_count}</td>
                      <td className="p-3">
                        {order.available_items}/{order.items_count}
                      </td>
                      <td className="p-3">
                        {order.missing_skus.length > 0
                          ? order.missing_skus.join(", ")
                          : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Filter Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex justify-between mb-4">
          <h2 className="text-lg font-medium">{t("merchantOrders.filters")}</h2>
          <Button onClick={handleClearFilters} icon={<X size={16} />}>
            {t("merchantOrders.clearFilters")}
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <FormInput
              name="order"
              type="text"
              leftIcon={<Search size={20} />}
              placeholder={t("merchantOrders.searchOrdersPlaceholder")}
              register={register}
              errors={errors}
              label={t("merchantOrders.search")}
            />
          </div>
          <div>
            <FormInput
              name="status"
              type="select"
              icon={<ChevronDown size={20} />}
              placeholder={t("merchantOrders.status")}
              register={register}
              errors={errors}
              selectOptions={statusOptions}
              label={t("merchantOrders.status")}
            />
          </div>
          <div>
            <FormInput
              name="date_type"
              type="select"
              icon={<ChevronDown size={20} />}
              placeholder={t("merchantOrders.dateType")}
              register={register}
              errors={errors}
              selectOptions={[
                { value: "created_at", label: t("merchantOrders.orderDate") },
                {
                  value: "delivery_date",
                  label: t("merchantOrders.deliveryDate"),
                },
              ]}
              label={t("merchantOrders.dateType")}
            />
          </div>
          <FormInput
            name="dateFrom"
            type="date"
            icon={<ChevronDown size={20} />}
            placeholder={t("merchantOrders.from")}
            register={register}
            errors={errors}
            label={t("merchantOrders.from")}
          />
          <FormInput
            name="dateTo"
            type="date"
            icon={<ChevronDown size={20} />}
            placeholder={t("merchantOrders.to")}
            register={register}
            errors={errors}
            label={t("merchantOrders.to")}
          />
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4">
        {isLoading ? (
          [...Array(8)].map((_, index) => (
            <div
              key={index}
              className="bg-white p-4 rounded-lg border border-gray-200"
            >
              <div className="text-gray-600 text-sm mb-2">
                <Skeleton width={80} />
              </div>
              <div className="text-2xl">
                <Skeleton width={40} />
              </div>
            </div>
          ))
        ) : (
          <>
            {[
              {
                label: t("merchantOrders.statusCards.readyForPackaging"),
                value: orders?.ready_for_packaging,
                color: "border-l-4 border-l-blue-500",
              },
              {
                label: t("merchantOrders.statusCards.onHold"),
                value: orders?.on_hold,
                color: "border-l-4 border-l-yellow-500",
              },
              {
                label: t("merchantOrders.statusCards.readyForShipping"),
                value: orders?.ready_for_shipping,
                color: "border-l-4 border-l-indigo-500",
              },
              {
                label: t("merchantOrders.statusCards.shipped"),
                value: orders?.shipped,
                color: "border-l-4 border-l-green-500",
              },
              {
                label: t("merchantOrders.statusCards.shipPartially"),
                value: orders?.ship_partially,
                color: "border-l-4 border-l-orange-500",
              },
              {
                label: t("merchantOrders.statusCards.handedOff"),
                value: orders?.handed_off,
                color: "border-l-4 border-l-purple-500",
              },
              {
                label: t("merchantOrders.statusCards.delivered"),
                value: orders?.delivered,
                color: "border-l-4 border-l-emerald-500",
              },
              {
                label: t("merchantOrders.statusCards.canceled"),
                value: orders?.canceled,
                color: "border-l-4 border-l-red-500",
              },
            ].map((card, index) => (
              <div
                key={index}
                className={`bg-white p-4 rounded-lg border border-gray-200 ${card.color}`}
              >
                <div className="text-gray-600 text-sm">{card.label}</div>
                <div className="text-2xl mt-1">{card.value ?? 0}</div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        {/* Desktop Table */}
        <table className="w-full hidden md:table">
          <thead className="bg-gray-50 border-b">
            <tr>
              <SortHeader
                field="id"
                label={t("merchantOrders.table.orderId")}
              />
              <SortHeader
                field="customer_name"
                label={t("merchantOrders.table.customer")}
              />
              <SortHeader field="type" label={t("merchantOrders.table.type")} />
              <SortHeader
                field="status"
                label={t("merchantOrders.table.status")}
              />
              <SortHeader
                field="total"
                label={t("merchantOrders.table.amount")}
              />
              <SortHeader
                field="created_at"
                label={t("merchantOrders.table.orderDate")}
              />
              <SortHeader
                field="delivery_date"
                label={t("merchantOrders.table.deliveryDate")}
              />
              <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t("merchantOrders.table.actions")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {isLoading
              ? [...Array(5)].map((_, index) => (
                  <tr key={index}>
                    {[...Array(8)].map((_, colIndex) => (
                      <td key={colIndex} className="p-3">
                        <Skeleton width={80} />
                      </td>
                    ))}
                  </tr>
                ))
              : orders?.data.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="p-3">{order.id}</td>
                    <td className="p-3">{order.customer_name}</td>
                    <td className="p-3">{order.type}</td>
                    <td className="p-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs ${getStatusColorClasses(
                          order.status
                        )}`}
                      >
                        {formatStatus(order.status)}
                      </span>
                    </td>
                    <td className="p-3">{order.total}</td>
                    <td className="p-3">{formatDate(order.created_at)}</td>
                    <td className="p-3">{formatDate(order.delivery_date)}</td>
                    <td className="p-3">
                      <button
                        onClick={() => handleOrderDetails(order.id)}
                        className="text-gray-600 hover:text-gray-900"
                      >
                        <Eye size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>

        {/* Mobile Cards */}
        <div className="md:hidden divide-y">
          {isLoading
            ? [...Array(5)].map((_, index) => (
                <div key={index} className="p-4">
                  <Skeleton height={100} />
                </div>
              ))
            : orders?.data.map((order) => (
                <div
                  key={order.id}
                  className="p-4 flex justify-between items-start hover:bg-gray-50"
                >
                  <div>
                    <div className="font-medium">
                      {t("merchantOrders.table.orderId")} #{order.id}
                    </div>
                    <div className="text-sm text-gray-500">
                      {order.customer_name}
                    </div>
                    <div className="text-sm text-gray-500">
                      {formatDate(order.created_at)}
                    </div>
                    <span
                      className={`inline-block mt-2 px-2 py-1 rounded-full text-xs ${getStatusColorClasses(
                        order.status
                      )}`}
                    >
                      {formatStatus(order.status)}
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="font-medium">JOD {order.total}</div>
                    <button
                      onClick={() => handleOrderDetails(order.id)}
                      className="text-gray-600 hover:text-gray-900 mt-2"
                    >
                      <Eye size={18} />
                    </button>
                  </div>
                </div>
              ))}
        </div>

        {/* Pagination */}
        <div className="bg-white px-4 py-3 flex flex-col sm:flex-row items-center justify-between border-t border-gray-200">
          <div className="text-sm text-gray-500 mb-2 sm:mb-0">
            {t("merchantOrders.pagination.showing")} {orders?.meta.from}-
            {orders?.meta.to} {t("merchantOrders.pagination.of")}{" "}
            {orders?.meta.total} {t("merchantOrders.pagination.entries")}
          </div>
          <div className="flex space-x-2">
            <button
              className="px-3 py-1 border rounded text-sm disabled:opacity-50"
              disabled={filters.page === 1}
              onClick={() => handlePageChange(filters.page - 1)}
            >
              {t("merchantOrders.pagination.previous")}
            </button>
            <button className="px-3 py-1 border rounded text-sm bg-blue-600 text-white">
              {filters.page}
            </button>
            <button
              className="px-3 py-1 border rounded text-sm disabled:opacity-50"
              disabled={filters.page === orders?.meta.last_page}
              onClick={() => handlePageChange(filters.page + 1)}
            >
              {t("merchantOrders.pagination.next")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MerchantOrders;
